import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  requestsService,
  type CommunityRequest,
  type CreateRequestPayload,
  type RequestListQuery,
  type UpdateRequestPayload,
} from '../../shared/services/requests.service';
import { extractPagination } from '../../shared/services/api-client';
import type { PaginationMeta } from '../../shared/types';

const PUBLIC_FEED_KEY = (params: RequestListQuery) =>
  ['community-requests', 'public', params] as const;
const MINE_KEY = (params: { page?: number; size?: number }) =>
  ['community-requests', 'mine', params] as const;
const DETAIL_KEY = (id: string) => ['community-requests', 'detail', id] as const;

/** Public feed of active requests. Filterable by city name (substring) and country. */
export function usePublicRequests(params: RequestListQuery = {}) {
  const query = useQuery({
    queryKey: PUBLIC_FEED_KEY(params),
    queryFn: () => requestsService.listPublic(params),
  });
  const requests: CommunityRequest[] = query.data?.data ?? [];
  const pagination: PaginationMeta | null = query.data
    ? extractPagination(query.data)
    : null;
  return {
    requests,
    pagination,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** A single request — used by the detail page. */
export function useRequest(requestId: string | null) {
  const query = useQuery({
    queryKey: DETAIL_KEY(requestId ?? ''),
    queryFn: () => requestsService.get(requestId!),
    enabled: Boolean(requestId),
  });
  return {
    request: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/** The viewer's own requests — for /my-requests. */
export function useMyRequests(params: { page?: number; size?: number } = {}) {
  const query = useQuery({
    queryKey: MINE_KEY(params),
    queryFn: () => requestsService.listMine(params),
  });
  return {
    requests: (query.data?.data ?? []) as CommunityRequest[],
    pagination: query.data ? extractPagination(query.data) : null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/** Mutations — create / update / resolve / delete / respond. Each invalidates
 *  the relevant query keys so list/detail pages refresh without manual juggling. */
export function useRequestMutations() {
  const qc = useQueryClient();

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['community-requests'] });
  };

  const create = useMutation({
    mutationFn: (payload: CreateRequestPayload) => requestsService.create(payload),
    onSuccess: invalidateAll,
  });

  const update = useMutation({
    mutationFn: (args: { id: string; payload: UpdateRequestPayload }) =>
      requestsService.update(args.id, args.payload),
    onSuccess: invalidateAll,
  });

  const resolve = useMutation({
    mutationFn: (id: string) => requestsService.resolve(id),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: (id: string) => requestsService.delete(id),
    onSuccess: invalidateAll,
  });

  const respond = useMutation({
    mutationFn: (args: { id: string; body?: string }) =>
      requestsService.respond(args.id, args.body),
    // Opening a chat thread doesn't change the request itself, but the
    // public response counter is derived from it — refresh the detail page.
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: DETAIL_KEY(args.id) });
      qc.invalidateQueries({ queryKey: ['community-requests', 'public'] });
    },
  });

  return { create, update, resolve, remove, respond };
}
